import CustomizationRequest from "../customization/request";
import BaseCustomization from "../customization/base";
import Customization from "../customization";
import { liquid } from "../customization/category";

// Connects to data-controller="site-builder--custom-images--actions"
export default class extends BaseCustomization {
  static targets = ["image"]
  static values = {
    type: String,
    key: String,
  }

  connect() {
    super.connect();
    this.emptyPreviewHolder = this.imageTarget.querySelector('.image-view.empty');
    this.filledPreviewHolder = this.imageTarget.querySelector('.image-view.filled');
  }

  get value(){
    return CustomizationRequest[`${this.typeValue}Image`]();
  }

  initializeSelection() {
    this.#apply(this.value)
  }

  #imageSetUrl() {
    let imageSet = liquid.imageSet || CustomizationRequest.getCurrentImageSet();
    if (!imageSet) {
      return;
    }

    const element = document.querySelector(`[data-image-set=${imageSet}] .image-section [data-image-set-placement="${this.typeValue}"]`);
    return element.dataset.originalUrl;
  }

  #apply(url) {
    if (url) {
      // Fill custom image section
      this.#setImagePreview(url);
    } else {
      const imageSetUrl = this.#imageSetUrl();
      if (imageSetUrl && url !== "") {
        this.#clearCropperDetails();
        // Custom image is not present, fill this section with image from Image set
        this.#apply(imageSetUrl);
      } else {
        // No image is available to display
        this.#removeImagePreview();
      }
    }
  }

  async change() {
    document.body.setAttribute('data-image-type', this.keyValue);
    await CustomizationRequest.turboFetch('/imageables/search_results');

    // Shift focus to modal
    document.activeElement.blur();
    const dialog = document.querySelector('.modal');
    dialog.classList.add("open");
  }

  remove() {
    Customization.serverUpdate({ [this.keyValue]: "" });
    this.dispatch('applied');
  }

  #setImagePreview(url) {
    this.filledPreviewHolder.style.backgroundImage = `url(${url})`;
    this.filledPreviewHolder.classList.add('visible');
    this.emptyPreviewHolder.classList.remove('visible');
  }

  #removeImagePreview() {
    this.filledPreviewHolder.style.cssText = "";
    this.filledPreviewHolder.classList.remove('visible');
    this.emptyPreviewHolder.classList.add('visible');
  }

  #clearCropperDetails() {
    const imageView = this.imageTarget.querySelector('.image-view');
    const hiddenDiv = imageView.querySelector('.image-cropper-details');

    if (hiddenDiv) {
      hiddenDiv.remove();
    }
  }

  async handleImageClick(event) {
    event.preventDefault();

    // For subsequent cropping target the correct image slot (hero, footer-one, footer-two)
    document.body.setAttribute('data-image-type', this.keyValue);

    const imageView = event.currentTarget.querySelector('.image-view');
    const hiddenDiv = imageView.querySelector('.image-cropper-details');
    if (!hiddenDiv) return;

    const cropperDetails = JSON.parse(hiddenDiv.getAttribute('data-cropper-details'));
    const largerImageUrl = hiddenDiv.getAttribute('data-large-image-url');
    const originalImageName = hiddenDiv.getAttribute('data-original-image-name');
    const activeTab = hiddenDiv.getAttribute('data-active-tab');

    const newImageURL = `/imageables/search_results?edit_cropper=true&active_tab=${activeTab}`;
    await CustomizationRequest.turboFetch(newImageURL);

    document.activeElement.blur();
    const dialog = document.querySelector('.modal');
    dialog.classList.add("open");

    this.#restoreEditState(activeTab, cropperDetails, largerImageUrl, originalImageName);
  }

  async #restoreEditState(activeTab, cropperDetails, largerImageUrl, originalImageName) {
    try {
      await this.#waitForElement('.modal-content .tabbed .tabbed__item');
      await this.#nextFrame();

      this.#activateTab(activeTab);

      await this.#restoreCropperState(activeTab, cropperDetails, largerImageUrl, originalImageName);
    } catch (error) {
      console.warn('Failed to restore edit state:', error);
    }
  }

  #activateTab(activeTab) {
    const activeTabs = document.querySelectorAll('.modal-content .tabbed .tabbed__item.active');
    activeTabs.forEach(tab => tab.classList.remove('active'));

    const tabToActivate = document.querySelector(
      `.modal-content .tabbed .tabbed__item[data-tabs-view-param="${activeTab}"]`
    );

    if (tabToActivate) {
      tabToActivate.click();
    } else {
      console.warn('Could not find tab to activate with data-tabs-view-param:', activeTab);
    }
  }

  async #restoreCropperState(activeTab, cropperDetails, largerImageUrl, originalImageName) {
    const tabConfig = this.#getTabConfig(activeTab);
    if (!tabConfig) return;

    await this.#waitForElement(tabConfig.element);

    this.#restoreZoomSlider(tabConfig.element, cropperDetails);

    const controller = this.application.getControllerForElementAndIdentifier(
      document.querySelector(tabConfig.element),
      tabConfig.controller
    );

    if (controller?.cropperOutlet) {
      await this.#restoreCropper(controller.cropperOutlet, cropperDetails, largerImageUrl, originalImageName);
    }
  }

  #getTabConfig(activeTab) {
    const configs = {
      'media-library': {
        element: '.media-library',
        controller: 'site-builder--custom-images--library-file-selector'
      },
      'upload': {
        element: '.upload-tab',
        controller: 'site-builder--custom-images--upload'
      },
      'stock-images': {
        element: '.stock-images',
        controller: 'site-builder--custom-images--stock'
      }
    };

    return configs[activeTab];
  }

  #restoreZoomSlider(elementSelector, cropperDetails) {
    const zoomSlider = document.querySelector(`${elementSelector} .crop-range`);
    if (zoomSlider && cropperDetails.sliderValue !== undefined) {
      zoomSlider.value = cropperDetails.sliderValue;
      const sliderColorPercentage = (zoomSlider.value - zoomSlider.min) / (zoomSlider.max - zoomSlider.min) * 100;
      zoomSlider.style.setProperty('--value', `${sliderColorPercentage}%`);
    }
  }

  async #restoreCropper(cropperOutlet, cropperDetails, largerImageUrl, originalImageName) {
    try {
      await cropperOutlet.use(largerImageUrl, originalImageName);

      const cropper = await this.#waitForCropperInstance(cropperOutlet);

      await this.#waitForCropperReady(cropper);

      if (cropperDetails.zoom && typeof cropperDetails.zoom === 'number') {
        cropper.zoomTo(cropperDetails.zoom);
        await this.#nextFrame();
      }
      cropper.setData(cropperDetails);
    } catch (error) {
      console.error('Error in restoreCropper:', error);
      throw error;
    }
  }

  #waitForElement(selector, timeout = 5000) {
    return new Promise((resolve, reject) => {
      const element = document.querySelector(selector);
      if (element) {
        resolve(element);
        return;
      }

      // Using MutationObserver for efficient DOM watching
      const observer = new MutationObserver((mutations, obs) => {
        const element = document.querySelector(selector);
        if (element) {
          obs.disconnect();
          resolve(element);
        }
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });

      setTimeout(() => {
        observer.disconnect();
        reject(new Error(`Element ${selector} not found within ${timeout}ms`));
      }, timeout);
    });
  }

  #waitForCropperInstance(cropperOutlet, timeout = 5000) {
    return new Promise((resolve, reject) => {
      if (cropperOutlet.cropper) {
        resolve(cropperOutlet.cropper);
        return;
      }

      const startTime = Date.now();
      const checkCropper = () => {
        if (cropperOutlet.cropper) {
          resolve(cropperOutlet.cropper);
        } else if (Date.now() - startTime > timeout) {
          reject(new Error('Cropper instance not ready within timeout'));
        } else {
          requestAnimationFrame(checkCropper);
        }
      };

      requestAnimationFrame(checkCropper);
    });
  }

  #waitForCropperReady(cropper, timeout = 5000) {
    return new Promise((resolve, reject) => {
      const startTime = Date.now();

      const checkReady = () => {
        try {
          const canvasData = cropper.getCanvasData();
          if (canvasData && canvasData.width > 0 && canvasData.height > 0) {
            resolve(cropper);
            return;
          }
        } catch {
          // Canvas not ready yet, continue checking
        }

        if (Date.now() - startTime > timeout) {
          reject(new Error('Cropper canvas not ready within timeout'));
        } else {
          requestAnimationFrame(checkReady);
        }
      };

      requestAnimationFrame(checkReady);
    });
  }

  #nextFrame() {
    return new Promise(resolve => requestAnimationFrame(resolve));
  }
}
